import React, { useState } from 'react';
import { Percent, Tag, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { getErrorMessage } from '../../utils/errorUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const CouponInput = ({ 
  amount, 
  subscriptionTier, 
  currency = "USD", 
  onDiscountApplied, 
  onDiscountRemoved,
  userId 
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const validateAndApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setIsValidating(true);
    try {
      const validationData = {
        coupon_code: couponCode.trim(),
        user_id: userId || 'anonymous',
        subscription_tier: subscriptionTier,
        amount: amount,
        currency: currency
      };

      const response = await axios.post(`${API}/api/coupons/validate`, validationData);
      const discountCalc = response.data;

      if (discountCalc.is_valid) {
        setAppliedCoupon({
          code: couponCode.trim().toUpperCase(),
          discount_amount: discountCalc.discount_amount,
          final_amount: discountCalc.final_amount,
          savings_percentage: discountCalc.savings_percentage,
          coupon: discountCalc.coupon
        });
        
        onDiscountApplied?.(discountCalc);
        setShowInput(false);
        
        toast.success(
          `Coupon applied! You saved $${discountCalc.discount_amount.toFixed(2)} (${discountCalc.savings_percentage.toFixed(1)}% off)`
        );
      } else {
        toast.error(discountCalc.error_message || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      toast.error(getErrorMessage(error, 'Failed to validate coupon'));
    } finally {
      setIsValidating(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    onDiscountRemoved?.();
    toast.info('Coupon removed');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      validateAndApplyCoupon();
    }
  };

  if (appliedCoupon) {
    return (
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
              <Tag className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <code className="font-mono font-medium text-green-800">
                  {appliedCoupon.code}
                </code>
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-sm text-green-700">
                {appliedCoupon.coupon?.name || 'Discount Applied'}
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="font-semibold text-green-800">
              -${appliedCoupon.discount_amount.toFixed(2)}
            </div>
            <div className="text-sm text-green-600">
              {appliedCoupon.savings_percentage.toFixed(1)}% off
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-green-200">
          <div className="text-sm text-green-700">
            {appliedCoupon.coupon?.description}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={removeCoupon}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>
      </div>
    );
  }

  if (!showInput) {
    return (
      <Button
        variant="outline"
        className="w-full justify-start text-left"
        onClick={() => setShowInput(true)}
      >
        <Tag className="h-4 w-4 mr-2" />
        Have a coupon code? Click to apply
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
            disabled={isValidating}
            className="font-mono"
          />
        </div>
        <Button 
          onClick={validateAndApplyCoupon}
          disabled={!couponCode.trim() || isValidating}
          className="px-6"
        >
          {isValidating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            'Apply'
          )}
        </Button>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <div className="text-gray-500">
          Enter your discount code and click apply
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowInput(false);
            setCouponCode('');
          }}
        >
          Cancel
        </Button>
      </div>
      
      {/* Popular Coupon Suggestions */}
      <div className="flex flex-wrap gap-2">
        <div className="text-xs text-gray-500">Popular codes:</div>
        {['SAVE20', 'NEWUSER', 'WELCOME'].map((code) => (
          <button
            key={code}
            onClick={() => setCouponCode(code)}
            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
          >
            {code}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CouponInput;